import type { TableUrlConfig } from '@repo/ui/components/molecules/infinite-table';
import type { AppRoutes } from '@/lib/models';
import type { FileRouteTypes } from '@/routeTree.gen';

/**
 * Liste con i pannelli su rotte figlie (`/new`, `/edit/$id`, `/$id`), come
 * nella webapp: il `_layout` rende la lista e l'`Outlet`, l'URL è la sola
 * fonte di verità del pannello e i filtri della lista passano di rotta
 * intatti. Un modulo dichiara le sue rotte come dati (`ListRoutes`) e usa gli
 * helper di questo file per aprire e chiudere i pannelli.
 */

/**
 * La `getRouteApi` del `_layout` che rende la lista, con l'id letterale:
 * `useSearch` restituisce la search tipizzata dallo schema della rotta.
 */
interface ListRouteApi<TSearch> {
  id: FileRouteTypes['id'];
  useSearch: () => TSearch;
}

export interface ListRoutes<TSearch = unknown> {
  api: ListRouteApi<TSearch>;
  list: AppRoutes;
  detail?: AppRoutes;
  create?: AppRoutes;
  edit?: AppRoutes;
}

/** Una stessa pagina montata su `/admin` e su `/referrer`. */
export interface AreaListRoutes<TAdmin, TReferrer> {
  admin: TAdmin;
  referrer: TReferrer;
}

export function pickAreaRoutes<TAdmin, TReferrer>(
  routes: AreaListRoutes<TAdmin, TReferrer>,
  isAdminOrSystem: boolean
): TAdmin | TReferrer {
  return isAdminOrSystem ? routes.admin : routes.referrer;
}

/**
 * Tabella e filtri scrivono sulla rotta CORRENTE (`'.'` del router, con i
 * suoi params): la lista resta montata dal layout anche mentre il pannello è
 * aperto, e la tabella scrive `skip`/`take` già al mount. Se puntasse alla
 * lista, quel primo `navigate` chiuderebbe il pannello aperto da un link.
 */
export const CURRENT_ROUTE_URL_SYNC: TableUrlConfig = { replace: true };

/**
 * La search verso il pannello: `true` la tiene com'è (convenzione del
 * router), una funzione la trasforma. La funzione è generica: il router
 * deve continuare a vedere il tipo di search della rotta di destinazione.
 */
type SearchTransform = <TSearch>(previous: TSearch) => TSearch;
export type PanelSearch = true | SearchTransform;
const KEEP_SEARCH = true as const;

/**
 * I filtri della lista senza i parametri che appartengono a un pannello
 * (es. `mode`/`sourceId` del form contratto): tornando alla lista l'indice
 * non deve riaprirlo.
 */
export function withoutSearchKeys(keys: readonly string[]): SearchTransform {
  return (previous) => {
    if (!previous || typeof previous !== 'object') return previous;
    const next = { ...previous } as Record<string, unknown>;
    for (const key of keys) delete next[key];
    return next as typeof previous;
  };
}

/** Apertura di un pannello senza parametri di rotta (creazione). */
export function buildPanelNavigation<
  TTo extends string,
  TSearch extends PanelSearch = true,
>(to: TTo, search?: TSearch) {
  return { to, search: search ?? KEEP_SEARCH };
}

/** Apertura del pannello di un'entità (`/$id`, `/edit/$id`). */
export function buildEntityPanelNavigation<
  TTo extends string,
  TSearch extends PanelSearch = true,
>(to: TTo, id: string, search?: TSearch) {
  return { to, params: { id }, search: search ?? KEEP_SEARCH };
}

/**
 * Chiusura del pannello: si torna alla stessa lista (filtri compresi) senza
 * aggiungere un passo alla cronologia.
 */
export function buildListNavigation<
  TTo extends string,
  TSearch extends PanelSearch = true,
>(to: TTo, search?: TSearch) {
  return { to, search: search ?? KEEP_SEARCH, replace: true as const };
}
